import {
  format,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';

import {
  getDefaultDatePickerMaxDate,
  getDefaultDatePickerMinDate,
} from '../components/common/forms/FormDatePicker.constants';
import { VALIDATION_MSG } from '../components/common/forms/validationMessages';

export const dateOnlyFormat = 'yyyy-MM-dd';

export const asDate = (value: unknown): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const parsed = parseISO(trimmed);
    if (isValid(parsed)) {
      return parsed;
    }

    const fallback = new Date(trimmed);
    return isValid(fallback) ? fallback : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
  }

  return null;
};

export const getDateBoundsError = (
  value: unknown,
  options?: {
    minDate?: Date | null;
    maxDate?: Date | null;
    minDateError?: string;
    maxDateError?: string;
  },
): string | null => {
  const date = asDate(value);

  if (!date) {
    return value === null || value === undefined || value === ''
      ? null
      : VALIDATION_MSG.invalidDate;
  }

  const normalizedDate = startOfDay(date);
  const minDate = options?.minDate ?? getDefaultDatePickerMinDate();
  const minDateValue = startOfDay(minDate);

  if (isBefore(normalizedDate, minDateValue)) {
    return (
      options?.minDateError ??
      `Date must be on or after ${format(minDateValue, 'P')}.`
    );
  }

  const maxDate = options?.maxDate ?? getDefaultDatePickerMaxDate();
  const maxDateValue = startOfDay(maxDate);
  if (isAfter(normalizedDate, maxDateValue)) {
    return (
      options?.maxDateError ??
      `Date must be on or before ${format(maxDateValue, 'P')}.`
    );
  }

  return null;
};

export const validateEndDate = (
  endDate: Date | null,
  startDate: Date | null,
): boolean => {
  if (!endDate || !startDate) {
    return true;
  }

  if (!isValid(endDate) || !isValid(startDate)) {
    return false;
  }

  return isEqual(endDate, startDate) || isAfter(endDate, startDate);
};
